// v1.28.09 — Role Permissions Manager (Super User only)
// Rows = actions · Columns = roles. Super User column is locked-on.
// Saves to /api/role-permissions and takes effect within ~5s (backend cache TTL).
import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { ShieldCheck, FloppyDisk, Lock, ArrowClockwise, WarningCircle } from "@phosphor-icons/react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function RolePermissions() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const isSU = !!user?.is_super_user;

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [actions, setActions] = useState([]);
    const [roles, setRoles] = useState([]);
    const [matrix, setMatrix] = useState({});
    const [dirty, setDirty] = useState(false);

    useEffect(() => {
        if (!isSU) return;
        loadMatrix();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isSU]);

    const loadMatrix = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/role-permissions");
            setActions(data.actions || []);
            setRoles(data.roles || []);
            setMatrix(data.matrix || {});
            setDirty(false);
        } catch (err) {
            toast.error(err?.message || "Failed to load permissions");
        } finally {
            setLoading(false);
        }
    };

    const grouped = useMemo(() => {
        const g = {};
        for (const a of actions) {
            const key = a.group || "Other";
            if (!g[key]) g[key] = [];
            g[key].push(a);
        }
        return g;
    }, [actions]);

    const toggle = (actionKey, roleKey) => {
        setMatrix((prev) => {
            const row = { ...(prev[actionKey] || {}) };
            row[roleKey] = !row[roleKey];
            return { ...prev, [actionKey]: row };
        });
        setDirty(true);
    };

    const save = async () => {
        if (!window.confirm("Save these permission changes?\n\nBackend caches settings for ~5 seconds, so changes will take effect within a few seconds of Save.")) return;
        setSaving(true);
        try {
            const { data } = await api.put("/role-permissions", { matrix });
            setMatrix(data.matrix || matrix);
            setDirty(false);
            toast.success("Permissions saved. Changes take effect within a few seconds.");
        } catch (err) {
            toast.error(err?.message || "Failed to save permissions");
        } finally {
            setSaving(false);
        }
    };

    if (!isSU) {
        return (
            <div className="mx-auto max-w-2xl p-8" data-testid="role-permissions-forbidden">
                <div className="flex items-start gap-3 rounded-lg border border-rose-200 bg-rose-50 p-4">
                    <WarningCircle size={22} weight="fill" className="mt-0.5 text-rose-600" />
                    <div>
                        <div className="text-sm font-bold text-rose-900">Super User access required</div>
                        <div className="mt-1 text-xs text-rose-700">Only Super Users can view or edit the Role Permissions matrix.</div>
                        <button
                            data-testid="back-btn"
                            onClick={() => navigate("/admin")}
                            className="mt-3 rounded bg-rose-600 px-3 py-1 text-xs font-semibold text-white hover:bg-rose-700"
                        >
                            Back to Dashboard
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="mx-auto max-w-6xl p-4 sm:p-6" data-testid="role-permissions-page">
            <header className="mb-5 flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                <div>
                    <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-indigo-600">
                        <ShieldCheck size={16} weight="fill" /> Super User only
                    </div>
                    <h1 className="mt-1 text-2xl font-black text-slate-900 sm:text-3xl">Role Permissions</h1>
                    <p className="mt-1 max-w-2xl text-xs text-slate-600 sm:text-sm">
                        Toggle what each role can do. The Super User column is locked-on. Save Changes writes to
                        the database; the app reads permissions live, so no code change is needed to grant or revoke access.
                    </p>
                </div>
                <div className="flex flex-shrink-0 gap-2">
                    <button
                        data-testid="reload-permissions-btn"
                        onClick={loadMatrix}
                        disabled={loading || saving}
                        className="inline-flex items-center gap-1.5 rounded border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                    >
                        <ArrowClockwise size={14} weight="bold" /> Reload
                    </button>
                    <button
                        data-testid="save-permissions-btn"
                        onClick={save}
                        disabled={!dirty || saving || loading}
                        className="inline-flex items-center gap-1.5 rounded bg-indigo-600 px-4 py-1.5 text-xs font-bold uppercase tracking-wider text-white shadow hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                        <FloppyDisk size={14} weight="fill" /> {saving ? "Saving…" : dirty ? "Save Changes" : "Saved"}
                    </button>
                </div>
            </header>

            {loading ? (
                <div className="rounded-lg border border-slate-200 bg-white p-8 text-center text-sm text-slate-500">
                    Loading permissions matrix…
                </div>
            ) : (
                <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white shadow-sm">
                    <table className="min-w-full text-sm">
                        <thead className="bg-slate-50">
                            <tr>
                                <th className="sticky left-0 z-10 bg-slate-50 px-3 py-2 text-left text-[11px] font-bold uppercase tracking-wider text-slate-600">
                                    Action
                                </th>
                                {roles.map((r) => (
                                    <th
                                        key={r.key}
                                        className={`px-3 py-2 text-center text-[11px] font-bold uppercase tracking-wider ${
                                            r.locked ? "bg-amber-100 text-amber-800" : "text-slate-600"
                                        }`}
                                    >
                                        <div className="flex flex-col items-center gap-0.5">
                                            <span>{r.label}</span>
                                            {r.locked && <Lock size={11} weight="bold" className="opacity-70" />}
                                        </div>
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {Object.entries(grouped).map(([group, items]) => (
                                <React.Fragment key={group}>
                                    <tr className="bg-slate-100">
                                        <td
                                            colSpan={roles.length + 1}
                                            className="px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.18em] text-slate-500"
                                        >
                                            {group}
                                        </td>
                                    </tr>
                                    {items.map((a) => (
                                        <tr key={a.key} className="border-t border-slate-100 hover:bg-slate-50/50">
                                            <td className="sticky left-0 z-10 bg-white px-3 py-2 text-xs font-medium text-slate-800">
                                                {a.label}
                                            </td>
                                            {roles.map((r) => {
                                                const checked = !!(matrix[a.key] || {})[r.key];
                                                const isLocked = r.locked;
                                                return (
                                                    <td
                                                        key={r.key}
                                                        className={`px-3 py-2 text-center ${isLocked ? "bg-amber-50/60" : ""}`}
                                                    >
                                                        <input
                                                            type="checkbox"
                                                            data-testid={`perm-${a.key}-${r.key}`}
                                                            checked={isLocked ? true : checked}
                                                            disabled={isLocked}
                                                            onChange={() => !isLocked && toggle(a.key, r.key)}
                                                            className="h-4 w-4 cursor-pointer rounded border-slate-300 text-indigo-600 focus:ring-indigo-500 disabled:cursor-not-allowed disabled:opacity-70"
                                                            aria-label={`${a.label} · ${r.label}`}
                                                        />
                                                    </td>
                                                );
                                            })}
                                        </tr>
                                    ))}
                                </React.Fragment>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            <p className="mt-4 text-[11px] leading-relaxed text-slate-500">
                <b>Note:</b> Delete Records is Super User only by default. If Admin's Delete Records is unchecked,
                admins will not be able to delete parties, transactions, uploads, etc. through the app. Toggle it on
                here to restore the older behaviour.
            </p>
        </div>
    );
}
